import { Form, FormField } from "@/components/ui/form";
import { ApiError, api } from "@/lib/api";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { type OpsUser, useAuthStore } from "./store";

interface LoginResponse {
  status: string;
  message: string;
  data: {
    user: OpsUser;
    session: { access: string; refresh: string; expires_at: string };
  };
}

interface Values {
  email: string;
  password: string;
}

export function LoginForm({ onSuccess }: { onSuccess: () => void }) {
  const form = useForm<Values>({ defaultValues: { email: "", password: "" } });
  const [error, setError] = useState<string | null>(null);

  async function submit(values: Values) {
    setError(null);
    try {
      const resp = await api<LoginResponse>("/auth/login/", {
        method: "POST",
        body: JSON.stringify(values),
        skipRefresh: true,
      });
      useAuthStore.getState().setUser(resp.data.user);
      onSuccess();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setError("Invalid credentials");
      else setError("Login failed — try again");
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-4">
        <FormField
          name="email"
          control={form.control}
          rules={{ required: "required" }}
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-1.5">
              <label className="iris-meta" htmlFor="login-email">
                Email
              </label>
              <input
                id="login-email"
                className="iris-input"
                type="email"
                autoComplete="email"
                aria-invalid={fieldState.error ? "true" : undefined}
                style={fieldState.error ? { borderColor: "var(--iris-neon-pink)" } : undefined}
                {...field}
              />
              {fieldState.error?.message && (
                <span style={{ fontSize: 12, color: "var(--iris-neon-pink)" }}>
                  {fieldState.error.message}
                </span>
              )}
            </div>
          )}
        />
        <FormField
          name="password"
          control={form.control}
          rules={{ required: "required" }}
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-1.5">
              <label className="iris-meta" htmlFor="login-password">
                Password
              </label>
              <input
                id="login-password"
                className="iris-input"
                type="password"
                autoComplete="current-password"
                aria-invalid={fieldState.error ? "true" : undefined}
                style={fieldState.error ? { borderColor: "var(--iris-neon-pink)" } : undefined}
                {...field}
              />
              {fieldState.error?.message && (
                <span style={{ fontSize: 12, color: "var(--iris-neon-pink)" }}>
                  {fieldState.error.message}
                </span>
              )}
            </div>
          )}
        />
        {error && <p style={{ fontSize: 12, color: "var(--iris-neon-pink)" }}>{error}</p>}
        <button
          type="submit"
          className="iris-btn iris-btn--primary mt-2"
          disabled={form.formState.isSubmitting}
        >
          {form.formState.isSubmitting ? "Signing in\u2026" : "Sign in"}
        </button>
      </form>
    </Form>
  );
}
